<script setup lang="ts">
import { AlertTriangle, ChevronDown, FileSearch, Loader2, RefreshCw, Search, ShieldCheck } from 'lucide-vue-next'
import type { WebChatMessage } from '~/types/web-chat'
import type { GroundingRecoveryAction } from '~~/shared/chat/groundingRecovery'

const props = defineProps<{
  validation: NonNullable<WebChatMessage['validation']>
  messageId: string
  isStreaming: boolean
}>()

// MAR-206: which recovery the user picked. `find_supporting` re-validates first, then asks the agent
// to source only what is still flagged (`composables/web-chat/useGroundingRecovery.ts`).
const emit = defineEmits<{
  'fix-validation': [messageId: string, action: GroundingRecoveryAction]
}>()

const { t } = useI18n()

type Claim = NonNullable<NonNullable<WebChatMessage['validation']>['claims']>[number]

const validationClaims = computed(() =>
  (props.validation.failedClaims ?? props.validation.claims)?.filter(claim => claim.verdict !== 'supported' && claim.verdict !== 'not_legal_claim') ?? [],
)

const checkedValidationClaims = computed(() =>
  (props.validation.checkedClaims ?? props.validation.claims)?.filter(claim => claim.verdict !== 'not_legal_claim') ?? [],
)

// Only ever surface friendly citation labels (e.g. "Art. 5 — Código…"). Never fall back to
// `claim.citations`, which are raw section UUIDs — leaking one into the UI was the reported bug.
function claimCitationLabels(claim: Claim) {
  return claim.citationLabels?.length ? claim.citationLabels : []
}

// Sort the flagged list highest severity first, so the claim needing the most attention isn't buried
// under lower-tier ones in a long list. `severity` is computed server-side (see
// `answerGroundingValidator.ts`'s `computeClaimSeverity`) — no client-side re-derivation.
const SEVERITY_RANK: Record<Claim['severity'], number> = { high: 0, medium: 1, low: 2, none: 3 }
const sortedValidationClaims = computed(() =>
  [...validationClaims.value].sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]),
)

const SEVERITY_LABEL_KEY: Record<Claim['severity'], string> = {
  high: 'webChat.validation.severity.high',
  medium: 'webChat.validation.severity.medium',
  low: 'webChat.validation.severity.low',
  none: 'webChat.validation.severity.none',
}
const SEVERITY_CLASSES: Record<Claim['severity'], string> = {
  high: 'border-destructive/40 bg-destructive/10 text-destructive',
  medium: 'border-warning-ink/40 bg-warning/20 text-warning-ink',
  low: 'border-border bg-muted/40 text-muted-foreground',
  none: 'border-border bg-muted/40 text-muted-foreground',
}
function severityLabel(claim: Claim) {
  return t(SEVERITY_LABEL_KEY[claim.severity])
}
function severityClasses(claim: Claim) {
  return SEVERITY_CLASSES[claim.severity]
}

const validationLabel = computed(() => {
  const status = props.validation.status
  if (status === 'running') return t('webChat.validation.checkingSources')
  if (status === 'passed') return t('webChat.validation.sourcesVerified')
  if (status === 'warning') return t('webChat.validation.needsSourceSupport')
  if (status === 'unavailable') return t('webChat.validation.unavailable')
  return ''
})
</script>

<template>
  <div class="mt-2 max-w-2xl">
    <div
      v-if="validation.status === 'running'"
      class="inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-[11px] text-muted-foreground"
    >
      <Loader2 class="h-3 w-3 animate-spin text-primary" />
      <span>{{ validationLabel }}</span>
    </div>

    <details
      v-else-if="validation.status === 'passed'"
      class="group/validation rounded-md border border-success/25 bg-success/5 text-[11px] text-success"
    >
      <summary class="flex cursor-pointer list-none items-center gap-1.5 px-2 py-1.5">
        <ShieldCheck class="h-3 w-3 shrink-0" />
        <span class="min-w-0 flex-1">{{ validationLabel }}</span>
        <ChevronDown class="h-3 w-3 shrink-0 transition-transform group-open/validation:rotate-180" />
      </summary>
      <div
        v-if="checkedValidationClaims.length > 0"
        class="border-t border-success/20 px-2 py-1.5"
      >
        <p class="mb-1.5 text-success/80">
          {{ t('webChat.validation.checkedClaims') }}
        </p>
        <div class="space-y-1.5">
          <div
            v-for="(claim, claimIdx) in checkedValidationClaims"
            :key="claimIdx"
            class="rounded-md border border-success/20 bg-background/50 p-1.5"
          >
            <p class="font-medium leading-snug">{{ claim.claim }}</p>
            <p class="mt-0.5 text-success/80">
              {{ claim.reason }}
            </p>
            <p v-if="claimCitationLabels(claim).length" class="mt-0.5 text-[10px] text-success/70">
              {{ claimCitationLabels(claim).join(', ') }}
            </p>
          </div>
        </div>
      </div>
    </details>

    <details
      v-else-if="validation.status === 'warning'"
      class="group/validation rounded-md border border-warning-ink/30 bg-warning/5 text-[11px] text-warning-ink"
    >
      <summary class="flex cursor-pointer list-none items-center gap-1.5 px-2 py-1.5">
        <AlertTriangle class="h-3 w-3 shrink-0" />
        <span class="min-w-0 flex-1">{{ validationLabel }}</span>
        <ChevronDown class="h-3 w-3 shrink-0 transition-transform group-open/validation:rotate-180" />
      </summary>
      <div class="border-t border-warning-ink/20 px-2 py-1.5">
        <div v-if="sortedValidationClaims.length > 0" class="space-y-1.5">
          <div
            v-for="(claim, claimIdx) in sortedValidationClaims"
            :key="claimIdx"
            class="rounded-md border border-warning-ink/20 bg-background/50 p-1.5"
          >
            <div class="flex items-start justify-between gap-2">
              <p class="min-w-0 flex-1 font-medium leading-snug">{{ claim.claim }}</p>
              <span
                class="shrink-0 rounded-full border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide"
                :class="severityClasses(claim)"
              >
                {{ severityLabel(claim) }}
              </span>
            </div>
            <p class="mt-0.5 text-warning-ink/80">
              {{ claim.reason }}
            </p>
            <p v-if="claimCitationLabels(claim).length" class="mt-0.5 text-[10px] text-warning-ink/70">
              {{ claimCitationLabels(claim).join(', ') }}
            </p>
          </div>
        </div>
        <button
          class="mt-2 inline-flex items-center gap-1 rounded-md border border-warning-ink/30 bg-background/70 px-2 py-1 text-[11px] font-medium text-warning-ink transition-colors hover:bg-warning/10 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="isStreaming"
          @click="emit('fix-validation', messageId, 'revise')"
        >
          <Search class="h-3 w-3" />
          <span>{{ t('webChat.validation.reviseAnswer') }}</span>
        </button>
        <button
          class="ml-1.5 mt-2 inline-flex items-center gap-1 rounded-md border border-warning-ink/30 bg-background/70 px-2 py-1 text-[11px] font-medium text-warning-ink transition-colors hover:bg-warning/10 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="isStreaming"
          data-testid="grounding-find-supporting"
          @click="emit('fix-validation', messageId, 'find_supporting')"
        >
          <FileSearch class="h-3 w-3" />
          <span>{{ t('webChat.validation.findSupporting') }}</span>
        </button>
      </div>
    </details>

    <div
      v-else-if="validation.status === 'unavailable'"
      class="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/30 px-2 py-1 text-[11px] text-muted-foreground"
    >
      <AlertTriangle class="h-3 w-3" />
      <span>{{ validationLabel }}</span>
      <button
        class="ml-1 inline-flex items-center gap-1 rounded border border-border bg-background/70 px-1.5 py-0.5 font-medium transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
        :disabled="isStreaming"
        data-testid="grounding-check-again"
        @click="emit('fix-validation', messageId, 'find_supporting')"
      >
        <RefreshCw class="h-3 w-3" />
        <span>{{ t('webChat.validation.checkAgain') }}</span>
      </button>
    </div>
  </div>
</template>
